import React from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { Elements } from '@stripe/react-stripe-js';
import stripePromise from '../stripe';
import PaymentForm from './PaymentForm';
import { 
  Crown, 
  Calendar, 
  CheckCircle2, 
  AlertCircle, 
  Zap,
  BarChart3,
  Shield,
  Users,
  Star
} from 'lucide-react';

const SubscriptionDemo = () => {
  const { currentUser } = useAuth();
  const { 
    subscription, 
    isTrialActive, 
    isSubscriptionActive, 
    getDaysLeftInTrial,
    isSpecialAccount,
    getSubscriptionStatus
  } = useSubscription();

  const daysLeft = getDaysLeftInTrial();
  const status = getSubscriptionStatus();

  return (
    <div className="min-h-screen bg-slate-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-light text-white mb-4">Subscription Demo</h1>
          <p className="text-slate-400 text-lg">See how the subscription system works</p>
        </div>

        {/* Current Status */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8 mb-8">
          <h2 className="text-2xl font-light text-white mb-6">Your Current Status</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-medium text-white mb-4">Account Info</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Email:</span>
                  <span className="text-white font-medium">{currentUser?.email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">User ID:</span>
                  <span className="text-white font-mono text-sm">{currentUser?.uid}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Status:</span>
                  <span className="text-white font-medium">
                    {status.message}
                  </span>
                </div>
                {isTrialActive() && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Days Left:</span>
                    <span className="text-white font-medium">{daysLeft} days</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Special Account:</span>
                  <span className="text-white font-medium">
                    {isSpecialAccount() ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-4">Subscription Details</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Plan:</span>
                  <span className="text-white font-medium">
                    {isSpecialAccount() ? 'Special Premium' : 'Basic'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Price:</span>
                  <span className="text-white font-medium">
                    {isSpecialAccount() ? 'Free' : '$5/month'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Trial Period:</span>
                  <span className="text-white font-medium">
                    {isSpecialAccount() ? 'N/A' : '7 days'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Active:</span>
                  <span className="text-white font-medium">
                    {isSubscriptionActive() ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Special Account Notice */}
        {isSpecialAccount() && (
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-2xl p-8 mb-8">
            <div className="flex items-center mb-4">
              <Star className="w-8 h-8 text-purple-400 mr-3" />
              <h2 className="text-2xl font-light text-white">Special Account</h2>
            </div>
            <p className="text-purple-300 text-lg mb-4">
              This account has been granted special access to all DeckLink features without requiring a subscription.
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                <span className="text-purple-300">Unlimited access to all features</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                <span className="text-purple-300">No payment required</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                <span className="text-purple-300">Priority support access</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                <span className="text-purple-300">Early access to new features</span>
              </div>
            </div>
          </div>
        )}

        {/* Features Comparison */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8 mb-8">
          <h2 className="text-2xl font-light text-white mb-6">What You Get</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white mb-4">Free Trial Features</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Basic pitch deck uploads</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Limited analytics</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Standard support</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white mb-4">Pro Features</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Crown className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span className="text-slate-300">Unlimited uploads</span>
                </div>
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span className="text-slate-300">Advanced analytics</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span className="text-slate-300">Priority support</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span className="text-slate-300">Team collaboration</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Zap className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span className="text-slate-300">Custom branding</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Section - Only show for non-special accounts */}
        {!isSpecialAccount() && !isSubscriptionActive() && (
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-light text-white mb-2">Upgrade to Pro</h2>
              <p className="text-slate-400">Get unlimited access to all features</p>
            </div>

            <Elements stripe={stripePromise}>
              <PaymentForm 
                onSuccess={() => console.log('Payment successful!')}
                onCancel={() => console.log('Payment cancelled')}
              />
            </Elements>
          </div>
        )}

        {/* Test Cards Info */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8 mt-8">
          <h3 className="text-lg font-medium text-white mb-4">Test Cards</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
              <div className="font-medium text-green-400 mb-1">Success</div>
              <div className="text-green-300 font-mono">4242 4242 4242 4242</div>
            </div>
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
              <div className="font-medium text-red-400 mb-1">Decline</div>
              <div className="text-red-300 font-mono">4000 0000 0000 0002</div>
            </div>
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
              <div className="font-medium text-yellow-400 mb-1">Insufficient Funds</div>
              <div className="text-yellow-300 font-mono">4000 0000 0000 9995</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDemo; 